import type { DemoHistoryInput, DemoHistoryJob } from '../types.js'
import type { DemoPlan, DemoScenario } from './demoPlan.js'
import { TRAINING_FACILITY, TRAINING_WORKER_IDS } from './trainingCast.js'

/** How often the onboarding screen asks how far the history has got. */
export const DEMO_HISTORY_POLL_MS = 2000
/** How long the screen waits for the history before giving up on it: well past the background function's 15 minutes. */
export const DEMO_HISTORY_GIVE_UP_MS = 20 * 60 * 1000

/**
 * What the history job is asked for (demo history plan H1): every facility with activity, each worker with the score
 * they behaved like month by month (the plan's arc, oldest first, ending with this month), and - where the plan
 * includes the training cast - its six scheduled every month with nothing reported for them, since their two months
 * are scripted and sent by the partner before the history starts. Null for a plan with no activity.
 */
export function buildDemoHistoryInput(plan: DemoPlan): DemoHistoryInput | null {
  if (plan.activeFacilities.length === 0) {
    return null
  }
  const castIsActive = plan.includesTrainingCast && plan.activeFacilities.some((facility) => facility.facilityId === TRAINING_FACILITY.facilityId)
  const trendOf = (scenario: DemoScenario | undefined): 'steady' | 'surge' | 'decline' => (scenario === 'winter_surge' ? 'surge' : scenario === 'census_decline' ? 'decline' : 'steady')
  return {
    months: plan.monthSpan,
    facilityIds: plan.activeFacilities.map((facility) => facility.facilityId),
    censusTrends: Object.fromEntries(plan.activeFacilities.map((facility) => [facility.facilityId, trendOf(plan.scenarios[facility.facilityId])])),
    monthsAhead: plan.monthsAhead,
    workers: [
      ...plan.activeWorkers.map((worker) => ({ workerId: worker.workerId, monthlyScores: [...worker.monthlyScores] })),
      ...(castIsActive ? TRAINING_WORKER_IDS.map((workerId) => ({ workerId, monthlyScores: null })) : []),
    ],
  }
}

/** What the months as worked came to, each 0 where an older API does not count it (before plan H2). */
export function workedCounts(counts: DemoHistoryJob['counts']): { swaps: number; covered: number; pickups: number; ranShort: number } {
  return { swaps: counts.swaps ?? 0, covered: counts.covered ?? 0, pickups: counts.pickups ?? 0, ranShort: counts.ranShort ?? 0 }
}

/** How far the history has got: months written of all it will write, across its facilities, and what those months are - the span, then the two months published ahead (next month often released already). */
export interface HistoryMonthsDone {
  done: number
  total: number
  facilities: number
  history: number
  ahead: number
}

export function historyMonthsDone(job: DemoHistoryJob): HistoryMonthsDone {
  return {
    done: job.facilities.reduce((sum, facility) => sum + facility.done.length, 0),
    total: job.periods.length * job.facilities.length,
    facilities: job.facilities.length,
    history: job.input.months,
    ahead: Math.max(0, job.periods.length - job.input.months),
  }
}

/** "3 of 8 months written (6 of history, 2 ahead)", or "... 8 at each of 4 facilities ..." where there are several. */
export function describeHistoryProgress(progress: HistoryMonthsDone): string {
  const each = progress.facilities > 1 ? `, ${progress.total / progress.facilities} at each of ${progress.facilities} facilities` : ''
  const what = progress.ahead > 0 ? ` (${progress.history} of history, ${progress.ahead} ahead)` : ''
  return `${progress.done} of ${progress.total} months written${each}${what}`
}

export interface DemoHistoryCalls {
  startDemoHistory(clientId: string, input: DemoHistoryInput): Promise<DemoHistoryJob>
  demoHistory(clientId: string): Promise<DemoHistoryJob>
}

/**
 * Starts the history and follows it to the end, telling `onProgress` each time it is read. It runs in the background
 * on the API (a year at several facilities takes minutes), so this only waits; closing the screen stops nothing.
 */
export async function runDemoHistory(api: DemoHistoryCalls, clientId: string, input: DemoHistoryInput, onProgress: (job: DemoHistoryJob) => void, wait: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms))): Promise<DemoHistoryJob> {
  let job = await api.startDemoHistory(clientId, input)
  onProgress(job)
  const giveUpAt = Date.now() + DEMO_HISTORY_GIVE_UP_MS
  while (job.status === 'running') {
    if (Date.now() > giveUpAt) {
      throw new Error('The history is taking longer than expected; it carries on in the background - open the client later to see it')
    }
    await wait(DEMO_HISTORY_POLL_MS)
    job = await api.demoHistory(clientId)
    onProgress(job)
  }
  if (job.status === 'failed') {
    throw new Error(job.error ?? 'Writing the history failed part way')
  }
  return job
}
